import React, { useState, useEffect } from 'react';
import {
  Users,
  MapPin,
  Calendar,
  Clock,
  Heart,
  Search,
  Filter,
  Plus,
  TrendingUp,
  Target,
  CheckCircle2,
  Loader2,
  AlertCircle
} from 'lucide-react';
import {
  volunteerService,
  opportunityService
} from '../../services/supabaseVolunteer';
import { EngagementOpportunity } from '../../types/volunteer.types';

interface VolunteerHubProps {
  memberId: string;
}

// Type adapte pour l'affichage
interface AdaptedOpportunity {
  id: string;
  title: string;
  description: string;
  type: string;
  location: string;
  date: string;
  volunteers_needed: number;
  volunteers_registered: number;
  skills: string[];
  status: 'open' | 'completed' | 'my_missions';
  urgent: boolean;
  originalOpportunity?: EngagementOpportunity;
}

// Composant CompactVolunteerCard
const CompactVolunteerCard: React.FC<{
  opportunity: AdaptedOpportunity;
  onViewDetails: (opportunity: AdaptedOpportunity) => void;
}> = ({ opportunity, onViewDetails }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-primary-green/10 text-primary-green border border-primary-green/20';
      case 'open': return 'bg-primary-blue/10 text-primary-blue border border-primary-blue/20';
      case 'my_missions': return 'bg-primary-yellow/10 text-primary-blue border border-primary-yellow/20';
      default: return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 size={14} />;
      case 'open': return <Heart size={14} />;
      default: return <Heart size={14} />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Terminee';
      case 'open': return 'Ouverte';
      case 'my_missions': return 'Mes missions';
      default: return 'En attente';
    }
  };

  const progress = Math.round((opportunity.volunteers_registered / opportunity.volunteers_needed) * 100);

  return (
    <div
      className="group card-udb p-6 hover:shadow-udb-lg hover:scale-105 transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-primary-blue/30"
      onClick={() => onViewDetails(opportunity)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 text-base line-clamp-2 leading-tight mb-3 group-hover:text-primary-blue transition-colors">
            {opportunity.title}
          </h3>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium ${getStatusColor(opportunity.status)}`}>
              {getStatusIcon(opportunity.status)}
              {getStatusText(opportunity.status)}
            </span>
            {opportunity.urgent && (
              <span className="bg-red-100 text-red-800 text-xs px-2 py-1 font-medium flex items-center gap-1">
                <span>!</span>
                Urgent
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin size={14} className="text-primary-blue flex-shrink-0" />
          <span className="truncate font-medium">{opportunity.location}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar size={14} className="text-primary-blue flex-shrink-0" />
          <span className="truncate">{new Date(opportunity.date).toLocaleDateString('fr-FR')}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users size={14} className="text-primary-blue flex-shrink-0" />
          <span className="truncate">{opportunity.volunteers_registered}/{opportunity.volunteers_needed} benevoles</span>
        </div>
      </div>

      {/* Progress bars */}
      <div className="space-y-2 mb-3">
        <div>
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span className="font-medium">Participation</span>
            <span className="font-semibold text-primary-blue">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 h-2 overflow-hidden">
            <div
              className={`h-2 transition-all duration-500 ${
                opportunity.status === 'completed' ? 'bg-primary-green' : 'bg-primary-blue'
              }`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VolunteerHub: React.FC<VolunteerHubProps> = ({ memberId }) => {
  const [activeTab, setActiveTab] = useState('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [opportunities, setOpportunities] = useState<AdaptedOpportunity[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [volunteerProfile, setVolunteerProfile] = useState<any>(null);
  const [stats, setStats] = useState<any>({ total_volunteers: 0, total_hours: 0, active_opportunities: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les donnees
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [
        activeOpportunities,
        profile,
        globalStats
      ] = await Promise.all([
        opportunityService.getActive(),
        memberId ? volunteerService.getByMemberId(memberId) : Promise.resolve(null),
        volunteerService.getStats().catch(() => ({ total_volunteers: 0, total_hours: 0, active_opportunities: 0 }))
      ]);

      // Adapter les opportunites pour l'affichage
      const adaptedOpportunities: AdaptedOpportunity[] = activeOpportunities.map(opp => ({
        id: opp.id?.toString() || '',
        title: opp.title,
        description: opp.description || '',
        type: opp.type || (opp as any).opportunity_type || 'event',
        location: (opp as any).constituencies?.display_name || opp.constituency?.displayName || 'Gabon',
        date: opp.start_date,
        volunteers_needed: opp.max_participants || 20,
        volunteers_registered: opp.current_participants || 0,
        skills: opp.required_skills || [],
        status: opp.status === 'completed' ? 'completed' : 'open',
        urgent: (opp as any).is_urgent || false,
        originalOpportunity: opp
      }));

      setOpportunities(adaptedOpportunities);
      setVolunteerProfile(profile);
      setStats(globalStats || { total_volunteers: 0, total_hours: 0, active_opportunities: activeOpportunities.length });
    } catch (err) {
      console.error('Erreur chargement benevolat:', err);
      setError('Impossible de charger les missions. Veuillez reessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpportunityClick = (opportunity: AdaptedOpportunity) => {
    console.log('Ouvrir mission:', opportunity);
    // TODO: Ouvrir modal de details ou page de participation
  };

  // Filtrer les opportunites
  const filteredOpportunities = opportunities.filter(opportunity => {
    const matchesTab =
      activeTab === 'all' ||
      (activeTab === 'open' && opportunity.status === 'open') ||
      (activeTab === 'completed' && opportunity.status === 'completed') ||
      (activeTab === 'my_missions' && opportunity.status === 'my_missions');

    const matchesSearch =
      !searchQuery ||
      opportunity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opportunity.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opportunity.location.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesTab && matchesSearch;
  });

  const statsDisplay = [
    { label: 'Missions ouvertes', value: stats.active_opportunities?.toString() || '0', icon: Heart },
    { label: 'Benevoles actifs', value: stats.total_volunteers?.toString() || '0', icon: Users },
    { label: 'Heures benevoles', value: stats.total_hours?.toString() || '0', icon: Clock },
    { label: 'Impact communautaire', value: '87%', icon: Target }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-blue animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des missions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Erreur</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadData} className="btn-udb-primary">
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header Section */}
      <section className="relative bg-primary-blue text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-float-up"
                style={{
                  width: Math.random() * 3 + 1 + 'px',
                  height: Math.random() * 3 + 1 + 'px',
                  left: Math.random() * 100 + '%',
                  bottom: '-20px',
                  backgroundColor: i % 3 === 0 ? '#ffd400' : i % 3 === 1 ? '#ffffff' : '#00469b',
                  opacity: Math.random() * 0.6 + 0.2,
                  animationDuration: `${Math.random() * 15 + 10}s`,
                  animationDelay: `${Math.random() * 10}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-light text-white">
                Ensemble pour
                <span className="text-primary-yellow font-normal ml-2">le changement</span>
              </h1>
              <p className="text-white/80 mt-1 font-light">
                Rejoignez notre communaute de benevoles engages
              </p>
            </div>

            <div className="hidden md:flex items-center gap-6">
              <div className="text-right">
                <div className="text-2xl font-light text-primary-yellow">
                  {filteredOpportunities.filter(o => o.status === 'open').length}
                </div>
                <div className="text-white/60 text-sm">Missions ouvertes</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-light text-primary-yellow">
                  {stats.total_hours || 124}
                </div>
                <div className="text-white/60 text-sm">Heures donnees</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation par onglets */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('open')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'open'
                  ? 'border-primary-blue text-primary-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Heart size={18} />
              Missions ouvertes
            </button>
            <button
              onClick={() => setActiveTab('my_missions')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'my_missions'
                  ? 'border-primary-yellow text-primary-yellow'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Heart size={18} />
              Mes missions
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'completed'
                  ? 'border-primary-green text-primary-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CheckCircle2 size={18} />
              Completees
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`py-6 px-2 border-b-2 font-medium text-base transition-colors flex items-center gap-2 ${
                activeTab === 'all'
                  ? 'border-primary-yellow text-primary-yellow'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users size={18} />
              Toutes
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 -mt-4">
          {statsDisplay.map((stat, index) => (
            <div key={index} className="card-udb p-6 text-center hover:shadow-udb-lg hover:scale-105 transition-all duration-300">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary-blue/10 rounded-full flex items-center justify-center">
                  <stat.icon className="text-primary-blue" size={24} />
                </div>
              </div>
              <div className="text-2xl font-light text-primary-blue mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="card-udb p-8 mb-8">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher une mission de benevolat..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-100 focus:border-primary-blue outline-none transition-all bg-white/70"
              />
            </div>
            <div className="flex items-center gap-2">
              <button className="flex items-center gap-2 px-6 py-3 border-2 border-gray-100 hover:border-primary-green hover:text-primary-green transition-all bg-white/70">
                <Filter size={20} />
                Filtres
              </button>
              {activeTab === 'open' && (
                <button className="btn-udb-primary flex items-center gap-2">
                  <Plus size={20} />
                  Proposer une mission
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Missions urgentes (seulement pour les missions ouvertes) */}
        {activeTab === 'open' && filteredOpportunities.filter(o => o.urgent).length > 0 && (
          <div className="mb-12">
            <div className="flex items-center space-x-2 mb-8">
              <TrendingUp className="w-6 h-6 text-primary-yellow" />
              <span className="text-primary-blue text-sm uppercase tracking-wider font-semibold">Missions urgentes</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8 leading-tight">
              Votre aide est <span className="text-primary-blue font-normal">precieuse</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredOpportunities.filter(o => o.urgent).map(opportunity => (
                <CompactVolunteerCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  onViewDetails={handleOpportunityClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Toutes les missions */}
        <div>
          <h2 className="text-2xl font-light text-gray-900 mb-8">
            {activeTab === 'all' ? 'Toutes les missions' :
             activeTab === 'open' ? 'Missions ouvertes' :
             activeTab === 'my_missions' ? 'Mes missions' : 'Missions completees'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredOpportunities.map(opportunity => (
              <CompactVolunteerCard
                key={opportunity.id}
                opportunity={opportunity}
                onViewDetails={handleOpportunityClick}
              />
            ))}
          </div>

          {filteredOpportunities.length === 0 && (
            <div className="text-center py-12">
              <Heart className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune mission trouvee</h3>
              <p className="text-gray-600">
                {searchQuery
                  ? `Aucune mission ne correspond a "${searchQuery}"`
                  : "Aucune mission disponible dans cette categorie"
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VolunteerHub;
